import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const ConfirmDialogContext = createContext(null);

function normalizeOptions(options) {
  if (typeof options === 'string') {
    return {
      title: 'Confirmar acción',
      description: options,
      confirmLabel: 'Aceptar',
      cancelLabel: 'Cancelar',
      variant: 'destructive',
      input: null,
      details: null,
    };
  }
  return {
    title: options?.title || 'Confirmar acción',
    description: options?.description || '',
    details: options?.details || null,
    confirmLabel: options?.confirmLabel || 'Aceptar',
    cancelLabel: options?.cancelLabel || 'Cancelar',
    variant: options?.variant || 'destructive',
    input: options?.input
      ? {
          label: options.input.label || 'Detalle',
          placeholder: options.input.placeholder || '',
          defaultValue: options.input.defaultValue || '',
          required: options.input.required !== false,
          multiline: Boolean(options.input.multiline),
        }
      : null,
  };
}

export function ConfirmDialogProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(() => normalizeOptions(''));
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    if (resolverRef.current) {
      resolverRef.current(opts?.input ? null : false);
      resolverRef.current = null;
    }
    const normalized = normalizeOptions(opts);
    setOptions(normalized);
    setInputValue(normalized.input?.defaultValue || '');
    setInputError('');
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOpen(false);
    setInputError('');
    if (resolve) resolve(value);
  }, []);

  const handleConfirm = () => {
    if (options.input) {
      const trimmed = String(inputValue || '').trim();
      if (options.input.required && !trimmed) {
        setInputError('Este campo es obligatorio');
        return;
      }
      settle(trimmed);
      return;
    }
    settle(true);
  };

  const confirmClass =
    options.variant === 'destructive'
      ? 'bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-600'
      : undefined;

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) settle(options.input ? null : false);
        }}
      >
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{options.title}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                {options.description ? (
                  typeof options.description === 'string' ? (
                    <p className="whitespace-pre-line">{options.description}</p>
                  ) : (
                    options.description
                  )
                ) : null}
                {Array.isArray(options.details) && options.details.length > 0 ? (
                  <ul className="list-disc pl-4 space-y-1 text-xs">
                    {options.details.map((item) => (
                      <li key={String(item)}>{item}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {options.input ? (
            <div className="space-y-1.5">
              <Label className="text-xs">{options.input.label}</Label>
              {options.input.multiline ? (
                <Textarea
                  className="text-sm min-h-[88px]"
                  value={inputValue}
                  placeholder={options.input.placeholder}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    if (inputError) setInputError('');
                  }}
                  autoFocus
                />
              ) : (
                <Input
                  className="h-9 text-sm"
                  value={inputValue}
                  placeholder={options.input.placeholder}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    if (inputError) setInputError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleConfirm();
                    }
                  }}
                  autoFocus
                />
              )}
              {inputError ? <p className="text-xs text-rose-600">{inputError}</p> : null}
            </div>
          ) : null}

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(options.input ? null : false)}>
              {options.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              className={confirmClass}
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
            >
              {options.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error('useConfirmDialog debe usarse dentro de ConfirmDialogProvider');
  }
  return ctx;
}
